import React, { useCallback, useEffect, useState } from 'react';
import { translate } from '../../i18n';
import ReusableSelect from '../../components/common/Select';
import { ILocation } from '../../type/location';
import DateSelector from '../../components/common/calendar/dateSelector';
import SunIcon from '../../assets/SunIcon';
import operationAPI from '../../api/operationApi';
import useLocationStore from '../../store/locatonStore';
import useDateStore from '../../store/selectedDateStore';
import useTemperatureStore from '../../store/temperatureStore';
import Card from '../../components/common/Header/Card';
import Loader from '../../assets/loader/Loader';
import { ITableHeader } from '../../interfaces/tableHeader';
import {
  passengerManifestHeader,
  passengerManifestHeaderKeyMapping,
} from '../../constants/headerConstants';

import {
  IPassengerManifestDTO,
  IWeatherDetail,
} from '../../type/passengerManifest';
import { IAddPaxOnFlight } from '../../type/addNotOnFlight';
import { toast } from 'react-toastify';
import {
  encodeDateForUrl,
  formatTimeToHHMM,
  getAdjustedDate,
} from '../../utils/dateUtils';
import passengerManifestApi from '../../api/passengerManifest';
import useBgColorStore from '../../store/bgColor';
import { ISelectedPassengerManifest } from '../../interfaces/selectedPassengerManifest';
import CalendarHeader from '../../components/common/Header/CalenderHeader';
import SunIconDark from '../../assets/SunIconDark';
import { showOrReplaceToast } from '../../utils/utility';
import ReusableSelectLocation from '../../components/common/SelectLocation';
const keyMapping: Record<string, string> = {
  id: 'id',
  haref: 'haRef',
  name: 'name',
  hotel: 'hotel',
  country: 'country',
};

interface ISite {
  altitude: string;
  value: string;
}

interface IPassenger {
  id: number;
  haRef: string;
  name: string;
  hotel: string;
  country: string;
  notOnThisFlight: boolean;
  bookingProductNameId: number;
  bookingProductChildId: number;
}

interface IBalloonFlight {
  pilotName: string;
  balloonTakeOfTime: string;
  operationBalloonFlightId: number;
  balloonLandTime: string;
  landSite: string;
  launchSite: string;
  passengers: IPassenger[];
  flightTime:string;
}

type PassengerDataState = Record<string, IBalloonFlight>;

const PassengerManifestDesktop = () => {
  const { bgColor, updateBgColor } = useBgColorStore();
  const [passengerData, setPassengerData] = useState<PassengerDataState>({});
  const keyMapping: Record<string, string> = passengerManifestHeaderKeyMapping;
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const { selectedDateFromStore, setSelectedDateFromStore } = useDateStore();
  const { selectedTemperature } = useTemperatureStore();
  const [locationList, setLocationList] = useState<ILocation[]>();
  const [selectedLocationId, setSelectedLocationId] = useState<string | number>(
    selectedLocation?.OwnerId,
  );
  const [selectedDate, setSelectedDate] = useState<Date>(
    selectedDateFromStore || getAdjustedDate(),
  );
  const [headers, setHeaders] = useState<ITableHeader[]>(
    passengerManifestHeader,
  );
  const [isPageLoading, setIsPageLoading] = useState(false);
  const tabs = Object.keys(passengerData);

  const setSelectedLocation = useLocationStore(
    (state) => state.setSelectedLocation,
  );
  const [activePilot, setActivePilot] = useState<string | null>(null);
  const [operationBalloonFlightId, setOperationBalloonFlightId] = useState<
    number | null
  >(null);
  const [balloonTakeOfTime, setBalloonTakeOfTime] = useState<string | null>(
    null,
  );
const[flightTime,setFlightTime]=useState<string|null>()
  const [weatherDetails, setWeatherDetails] = useState<IWeatherDetail>();

  const [selectedPassenger, setSelectedPassenger] = useState<{
    [key: string]: ISelectedPassengerManifest;
  }>({});

  const [launchSite, setLaunchSite] = useState<ISite[]>();
  const [landSite, setLandSite] = useState<ISite[]>();

  const fetchOwnerLocations = useCallback(async () => {
    try {
      const response = await operationAPI.getLocationList();
      setLocationList(response?.data);
    } catch (error) {
      console.log(error);
    }
  }, []);
  
    useEffect(() => {
      setSelectedDate(selectedDateFromStore);
    }, [selectedDateFromStore]);

  const fetchPassengerManifest = useCallback(async () => {
    setIsPageLoading(true);
    try {
      const encodedDate = encodeDateForUrl(selectedDate);
      const response = await passengerManifestApi.getPassengerManifest(
        encodedDate,
        selectedLocation.OwnerId,
      );
      setIsPageLoading(false);
      setWeatherDetails(response?.data?.weatherDetails);
      const formattedData = transformApiResponse(response?.data.bookingDetails);
      setPassengerData(formattedData);
      if (Object.keys(formattedData).length > 0) {
        setActiveTab(Object.keys(formattedData)[0]);
      }
      const tabs = Object.keys(formattedData);
      if (tabs.length > 0) {
        setActiveTab(tabs[0]);
        setActivePilot(formattedData[tabs[0]]?.pilotName);
        setFlightTime(formattedData[tabs[0]]?.flightTime)
        setBalloonTakeOfTime(
          new Date(formattedData[tabs[0]]?.balloonTakeOfTime)
            .toISOString()
            .substring(11, 16),
        );
        setOperationBalloonFlightId(
          formattedData[tabs[0]]?.operationBalloonFlightId,
        );
        setLunchAndLandSite(formattedData[tabs[0]]);
      } else {
        setActiveTab(null);
        setLunchAndLandSite(formattedData[tabs[0]]);
      }
    } catch (error) {
      console.log(error);
      setIsPageLoading(false);
    }
  }, [selectedLocation.OwnerId, selectedDate]);

  const setLunchAndLandSite = (balloonDetails: IBalloonFlight) => {
    setLaunchSite([
      {
        altitude: 'Location',
        value:
          balloonDetails?.launchSite ??
          translate('operation.flightDetails.blankSpace'),
      },
      {
        altitude: 'Time',
        value: balloonDetails?.balloonTakeOfTime
          ? formatTimeToHHMM(balloonDetails?.balloonTakeOfTime)
          : translate('operation.flightDetails.timeBlankSpace'),
      },
    ]);
    setLandSite([
      {
        altitude: 'Location',
        value:
          balloonDetails?.landSite ??
          translate('operation.flightDetails.blankSpace'),
      },
      {
        altitude: 'Time',
        value: balloonDetails?.balloonLandTime
          ? formatTimeToHHMM(balloonDetails?.balloonLandTime)
          : translate('operation.flightDetails.timeBlankSpace'),
      },
    ]);
  };

  const transformApiResponse = (
    data: any[],
  ): Record<string, IBalloonFlight> => {
    return data.reduce(
      (acc, balloon) => {
        const balloonName = balloon.balloonName;
        if (!acc[balloonName]) {
          acc[balloonName] = {
            pilotName: balloon.pilotName,
            balloonTakeOfTime: balloon.balloonTakeOfTime,
            operationBalloonFlightId: balloon.operationBalloonFlightId,
            balloonLandTime: balloon?.balloonLandTime,
            landSite: balloon?.landSite,
            launchSite: balloon?.launchSite,
            flightTime:balloon?.flightTime,
            passengers: [],
          };
        }
        balloon.bookings.forEach((booking: any, index: number) => {
          acc[balloonName].passengers.push({
            id: index + 1,
            haRef: booking?.bookingId,
            name: booking?.paxName,
            hotel: booking?.pickup,
            country: booking?.country,
            notOnThisFlight: booking?.notOnFlight,
            bookingProductNameId: booking?.bookingProductNameId,
            bookingProductChildId: booking?.bookingProductChildId,
          });
        });

        return acc;
      },
      {} as Record<
        string,
        {
          pilotName: string;
          passengers: {
            id: number;
            haRef: string;
            name: string;
            hotel: string;
            country: string;
            notOnThisFlight: boolean;
          }[];
        }
      >,
    );
  };

  const handleLocationChange = (selectedOption: ILocation) => {
    setSelectedLocationId(selectedOption.OwnerId);
    setSelectedLocation(selectedOption);
    updateBgColor();
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setSelectedDateFromStore(date);
    }
  };
  const toggleEditMode = () => {
    setIsEditMode((prev) => !prev);
  };
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setActivePilot(passengerData[tab]?.pilotName || null);
    setBalloonTakeOfTime(
      new Date(passengerData[tab]?.balloonTakeOfTime)
        .toISOString()
        .substring(11, 16) || null,
    );
    setOperationBalloonFlightId(passengerData[tab]?.operationBalloonFlightId);
    setLunchAndLandSite(passengerData[tab]);
    setFlightTime(passengerData[tab]?.flightTime)
  };
  const addPaxNotInFlight = async (data: IAddPaxOnFlight) => {
    try {
      const response = await passengerManifestApi.addNotOnFlight(data);
      showOrReplaceToast(
        'paxNotOnFlight',
        translate(response?.data),
        'success',
      );
    } catch (error) {
      console.log(error);
      toast.warn(translate('operation.commonError'));
    }
  };

  const handleCheckboxChange = (data: IAddPaxOnFlight) => {
    setSelectedPassenger((prev) => {
      const updatedPassengers = { ...prev };
      const id = data?.bookingProductNameId ?? data?.bookingProductChildId;
      if (!id) return prev;

      if (updatedPassengers[id]) {
        delete updatedPassengers[id];
      } else {
        const paxData = {
          operationBalloonFlightId: data?.operationBalloonFlightId ?? 0,
          ...(data?.bookingProductNameId
            ? { bookingProductNameId: data?.bookingProductNameId }
            : {}),
          ...(data?.bookingProductChildId
            ? { bookingProductChildId: data?.bookingProductChildId }
            : {}),
          isChecked: true,
        };
        updatedPassengers[id] = { ...paxData };
      }
      return updatedPassengers;
    });

    addPaxNotInFlight(data);
  };

  const updateSelectedPassengers = (bookingDetails: PassengerDataState) => {
    setSelectedPassenger((prev) => {
      const updatedPassengers = { ...prev };

      Object.values(bookingDetails)?.forEach((balloon) => {
        balloon.passengers?.forEach((passenger) => {
          if (passenger?.notOnThisFlight) {
            const id =
              passenger?.bookingProductNameId ||
              passenger?.bookingProductChildId;
            if (!id) return;

            updatedPassengers[id] = {
              operationBalloonFlightId: balloon?.operationBalloonFlightId,
              bookingProductNameId: passenger?.bookingProductNameId,
              bookingProductChildId: passenger?.bookingProductChildId,
              isChecked: true,
            };
          }
        });
      });

      return updatedPassengers;
    });
  };
  useEffect(() => {
    updateSelectedPassengers(passengerData);
  }, [passengerData]);

  useEffect(() => {
    fetchPassengerManifest();
  }, [fetchPassengerManifest, selectedLocation.OwnerId, selectedDate]);

  useEffect(() => {
    fetchOwnerLocations();
  }, [fetchOwnerLocations]);

  const Metar = [
    { altitude: 'Temperature', value: weatherDetails?.temperature1 ?? '-' },
    { altitude: 'Pressure', value: weatherDetails?.pressure ?? 'N/A' },
    { altitude: 'SWSD', value: weatherDetails?.swsd ?? 'N/A' },
  ];

  const windData = [
    { altitude: '1000', value: weatherDetails?.wind1 ?? '-' },
    { altitude: '2000', value: weatherDetails?.wind2 ?? '-' },
    { altitude: '3000', value: weatherDetails?.wind3 ?? '-' },
    { altitude: '5000', value: weatherDetails?.wind4 ?? '-' },
  ];

  return (
    <div style={{ textAlign: 'center' }}>
      {
        <div className={'bg-background min-h-screen px-6'}>
          <div className=" bg-background">
            <CalendarHeader />
          </div>
          <div className="text-start flex justify-between">
            <div>
              <h1 className="text-lg font-semibold text-textColorGray">
                {translate('passengerManifest.PassengerManifest')}
              </h1>
            </div>
          </div>
          <div className="bg-background rounded-lg flex items-end justify-between mt-2">
            <div className="flex">
              <div className="text-start mr-4">
                <label className="text-textColor">
                  {translate('common.location')}
                </label>
                <ReusableSelectLocation<ILocation>
                  options={(locationList || []) as ILocation[]}
                  className="w-full border border-customBorder-border rounded p-2 text-sm  bg-sidebar-background text-text-primary"
                  getOptionLabel={(option: ILocation) =>
                    `${option.locationName}`
                  }
                  getOptionValue={(option: ILocation) => option.OwnerId}
                  onChange={handleLocationChange}
                  placeholder="Select a locations"
                  value={selectedLocationId}
                />
              </div>
              <div className="text-start mr-4">
                <label className="text-textColor">
                  {translate('common.date')}
                </label>
                <div className="flex items-center h-[38px] p-2 rounded border  border-customBorder-border bg-sidebar-background text-text-primary">
                  <DateSelector
                    onDateChange={handleDateChange}
                    initialDate={selectedDate}
                    showArrows={true}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Tab */}
          <div className="bg-background mt-6">
            <div className=" flex space-x-6">
              {tabs.map((tab) => (
                <span
                  key={tab}
                  className={`cursor-pointer p-2 ${
                    activeTab === tab
                      ? `${bgColor} text-textColor font-semibold border-b-2 border-blue-600`
                      : 'text-textColorGray'
                  }`}
                  onClick={() => handleTabChange(tab)} // ✅ Updated here
                >
                  {`Balloon VH- ` + tab}
                </span>
              ))}
            </div>
            <div className={`${bgColor}`}>
              <div className="flex p-4 text-left text-base w-72 justify-between">
                <div>
                  <p className="font-normal text-textColorGray">
                    {translate('passengerManifest.Pilot')}
                  </p>
                  <p className="font-semibold text-textColor">{activePilot}</p>
                </div>
                <div>
                  <p className="font-normal text-textColorGray">
                    {translate('passengerManifest.flightTime')}
                  </p>
                  <p className="font-semibold text-textColor">
                    {flightTime}
                  </p>
                </div>
              </div>
              <div className="p-4  grid grid-cols-4 gap-4">
                <Card
                  title={`${translate('passengerManifest.winds')}`}
                  data={windData}
                  labelKey="altitude"
                  valueKey="value"
                  className="grid-cols-4 gap-4"
                />

                <Card
                  title={`${translate('passengerManifest.meter')}`}
                  data={Metar}
                  labelKey="altitude"
                  valueKey="value"
                  className="grid-cols-3 gap-4"
                />

                <Card
                  title={`${translate('passengerManifest.launchSite')}`}
                  labelKey="altitude"
                  valueKey="value"
                  className="grid-cols-2 gap-4"
                  data={launchSite}
                />

                <Card
                  title={`${translate('passengerManifest.landingSite')}`}
                  labelKey="altitude"
                  valueKey="value"
                  className="grid-cols-2 gap-4"
                  data={landSite}
                />
              </div>

              {/* Table */}
              <div className={`${bgColor} overflow-x-auto p-4`}>
                <table className="min-w-full border-collapse border border-customBorder-tableBorder shadow-lg text-left">
                  <thead className="bg-[#004E9B] text-white">
                    <tr className="text-sm">
                      {headers?.slice(0, 3).map((header, index) => (
                        <th
                          key={index}
                          className="border border-customBorder-border p-3 text-sm"
                        >
                          {header.label}
                        </th>
                      ))}

                      {headers?.slice(3, 6).map((header, index) => (
                        <th
                          key={index}
                          className="border border-customBorder-border p-3 text-sm"
                        >
                          {header.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isPageLoading ? (
                      <tr>
                        <td
                          colSpan={headers?.length || 1}
                          className="text-center p-4 h-[200px] text-lg font-semibold"
                        >
                          <div className="inline-grid">
                            <Loader width={50} height={50} />
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <>
                        {activeTab && passengerData[activeTab] ? (
                          passengerData[activeTab]?.passengers.map(
                            (row, index) => (
                              <tr
                                key={index}
                                className={`${index % 2 === 0 ? 'bg-backgroundColor-white' : 'bg-gray-100'} text-text-primary`}
                              >
                                {headers?.map((header, headerIndex) => {
                                  const filterKey = header.label
                                    ?.replace(/\s+/g, '')
                                    .toLowerCase();
                                  const rowKey = keyMapping[filterKey];

                                  return (
                                    <td
                                      key={headerIndex}
                                      className="border-gray-300 p-3"
                                    >
                                      {header.label === 'Not On This Flight' ? (
                                        <div className="flex justify-center">
                                          <input
                                            type="checkbox"
                                            className="w-4 h-4 cursor-pointer"
                                            checked={Boolean(
                                              selectedPassenger[
                                                row?.bookingProductNameId ||
                                                  row?.bookingProductChildId
                                              ]?.isChecked,
                                            )}
                                            onChange={(e) => {
                                              const data: IAddPaxOnFlight = {
                                                operationBalloonFlightId:
                                                  operationBalloonFlightId,
                                                bookingProductNameId:
                                                  row.bookingProductNameId,
                                                bookingProductChildId:
                                                  row?.bookingProductChildId,
                                                isPaxDeleted: e.target.checked,
                                              };
                                              handleCheckboxChange(data);
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        (row[rowKey as keyof typeof row] ?? '-')
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ),
                          )
                        ) : (
                          <tr className="text-text-primary">
                            <td
                              colSpan={(headers?.length || 1) + 1}
                              className="text-center p-4"
                            >
                              {translate('passengerManifest.dataNotAvailable')}
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default PassengerManifestDesktop;
